import { memo, useMemo, useState } from "react";
import { Edit3, Upload as UploadIcon } from "lucide-react";
import { TIMELINE_BAR_GRID, padCh, trimName } from "./dawHelpers";

/* Timeline wave lane — one row per channel, shows downsampled waveform + playhead.
 * Wrapped in React.memo so the 24 lanes don't all rerender when ONE of them
 * changes (e.g. selection moves, playhead pct ticks). Each lane only rerenders
 * when its own props shift. Profile-verified: cuts ~70% of lane VDOM work
 * during playback on a 64-channel session. */
function WaveLane({ index, selected, channel, tint, playheadPct, onSelect, onOpenClipEditor, recordingMeter, onDropFile, hasBuffer, channelName, waveformRev, playing = false, liveRecSamples = null, livePlaySamples = null, clipping = false, clippedRecently = false, liveWaveRev = 0 }) {
  void liveWaveRev; // only here to trigger re-render via memo equality check
  const [dragOver, setDragOver] = useState(false);
  const wave = useMemo(
    () => channel?.waveform || Array.from({ length: 120 }, (_, i) => 0.04 + Math.abs(Math.sin((i + 1) * (index + 2) * 0.19)) * 0.18),
    [channel?.waveform, index]
  );
  const playheadStyle = useMemo(() => ({ left: `${playheadPct}%` }), [playheadPct]);

  // Recording overlay — when this channel is being recorded into, paint a
  // growing red strip whose width = elapsed seconds (capped to lane width)
  // and a tiny live peak meter pinned to the right edge so the user has
  // immediate visual proof the mic is hot. Capped at 60 seconds visual span
  // to keep the strip readable; longer takes still record fine, the bar just
  // saturates the lane.
  const isRecording = !!recordingMeter;
  const isClipping = !!recordingMeter?.clipping;
  const recPct = isRecording ? Math.min(100, (recordingMeter.sec / 60) * 100) : 0;
  const recPeakPct = isRecording ? Math.min(100, recordingMeter.peak * 100) : 0;
  // iter-151 — Numeric dBFS readout next to the gradient peak meter.
  // Visually tells the user EXACTLY how hot their input is, without
  // having to estimate from a coloured bar. Clamped to -60..0 for
  // readability; values below -60 show as -∞ so a silent mic is
  // immediately obvious.
  const recPeakDb = isRecording
    ? (recordingMeter.peak < 0.001 ? "−∞" : Math.max(-60, 20 * Math.log10(recordingMeter.peak)).toFixed(1))
    : null;
  // Tone: green well below -12 dBFS, amber -12..-3, rose -3..0 (clip range).
  const recPeakDbTone = isRecording
    ? (recordingMeter.peak >= 0.71 ? "text-rose-200"   // ≥ -3 dBFS
      : recordingMeter.peak >= 0.25 ? "text-amber-200" // ≥ -12 dBFS
      : "text-emerald-200")
    : "";

  return (
    <div
      onClick={() => onSelect(index)}
      data-testid={`wave-lane-${index}`}
      onDragOver={(e) => {
        // Audio files come in as `Files` kind — accept those; ignore HTML drags.
        if (!onDropFile) return;
        const hasFile = Array.from(e.dataTransfer.items || []).some((it) => it.kind === "file");
        if (!hasFile) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "copy";
        if (!dragOver) setDragOver(true);
      }}
      onDragLeave={(e) => {
        // Only deactivate when the cursor actually leaves the lane bounds, not
        // when it crosses inner children (which fire dragleave on the parent).
        if (e.currentTarget.contains(e.relatedTarget)) return;
        setDragOver(false);
      }}
      onDrop={(e) => {
        if (!onDropFile) return;
        const files = e.dataTransfer.files;
        if (!files || files.length === 0) return;
        e.preventDefault();
        setDragOver(false);
        // Per-lane drop accepts only the FIRST file — multi-file drops should
        // hit the timeline-level handler that fans them out across channels.
        onDropFile(index, files[0]);
      }}
      className={`mb-2 flex cursor-pointer items-center gap-3 rounded-xl border px-3 py-2 transition ${
        dragOver
          ? "border-fuchsia-300/80 bg-fuchsia-500/[0.13] ring-2 ring-fuchsia-300/60 shadow-[0_0_24px_rgba(232,121,249,0.32)]"
          : selected ? "border-white/40 bg-white/[0.09]" : "border-white/10 bg-white/[0.025] hover:bg-white/[0.05]"
      } ${isRecording ? "ring-1 ring-rose-400/60 shadow-[0_0_18px_rgba(244,63,94,0.18)]" : ""} ${isClipping ? "wave-lane-clip-flash" : ""} ${hasBuffer && playing ? "lane-edge-sheen" : ""}`}
    >
      <div className="flex w-24 items-center gap-2">
        <span className={`h-2 w-2 rounded-full ${isRecording ? "bg-rose-400 animate-pulse" : (channel?.buffer ? tint.dot : "bg-white/20")}`} />
        <span className="truncate font-mono text-[11px] uppercase tracking-wider text-white/65">
          {padCh(index)} · {trimName(channel?.name, 10) || "empty"}
        </span>
        {clippedRecently && (
          <span
            data-testid={`wave-lane-clipped-${index}`}
            title="This channel clipped recently — bring the fader down."
            className="ml-1 rounded-full bg-red-500/90 px-1.5 py-[1px] font-mono text-[8.5px] font-bold uppercase tracking-wider text-white shadow-[0_0_6px_rgba(239,68,68,0.8)] animate-pulse"
          >Clip</span>
        )}
      </div>
      <div className="relative h-12 flex-1 overflow-hidden rounded-lg bg-black/40 ring-1 ring-white/5">
        <div className="pointer-events-none absolute inset-0 flex">
          {Array.from({ length: TIMELINE_BAR_GRID }).map((_, i) => (
            <div key={`lane-${index}-grid-${i}`} className="flex-1 border-r border-white/[0.04]" />
          ))}
        </div>
        <div className="flex h-full items-center gap-[2px] px-2">
          {wave.map((v, i) => (
            <span
              key={`lane-${index}-bin-${i}`}
              className={`w-[2px] rounded-full ${channel?.buffer ? tint.dot : `bg-white/25 ${playing ? "lane-empty-ghost" : ""}`}`}
              style={{ height: `${Math.max(8, v * 90)}%`, opacity: channel?.buffer ? 0.9 : 0.55 }}
            />
          ))}
        </div>
        {/* iter-197 — Live playback waveform overlay. Shows the actual
            amplitude flowing through this channel right now (sampled at
            ~20 Hz from the engine's per-channel analyser). Sits ABOVE
            the static waveform so the user sees both: the song shape
            (faded) + the live activity (vivid). */}
        {playing && livePlaySamples && livePlaySamples.length > 0 && (
          <div
            data-testid={`wave-lane-live-play-${index}`}
            className="pointer-events-none absolute inset-y-0 left-0 right-0 flex items-center gap-[1.5px] px-2"
          >
            {livePlaySamples.map((v, i) => (
              <span
                key={`lp-${index}-${i}`}
                className={`w-[2px] rounded-full ${clipping ? "bg-red-300 shadow-[0_0_4px_rgba(252,165,165,0.95)]" : "bg-cyan-200/95 shadow-[0_0_3px_rgba(165,243,252,0.7)]"}`}
                style={{ height: `${Math.max(4, v * 90)}%` }}
              />
            ))}
          </div>
        )}
        {/* Live recording overlay — sits ABOVE the static waveform so users see
            the red region growing rightward as they record.
            iter-197 — the rectangular fill is replaced with bars drawn from
            the rolling peak history so the user sees the ACTUAL incoming
            waveform shape, not just a generic red band. */}
        {isRecording && (
          <>
            <div
              data-testid={`wave-lane-rec-fill-${index}`}
              className="pointer-events-none absolute inset-y-0 left-0 bg-rose-500/15 ring-1 ring-inset ring-rose-400/30 transition-[width] duration-100 ease-linear"
              style={{ width: `${recPct}%` }}
            />
            {liveRecSamples && liveRecSamples.length > 0 && (
              <div
                data-testid={`wave-lane-live-rec-${index}`}
                className="pointer-events-none absolute inset-y-0 left-0 flex items-center gap-[1.5px] px-2 transition-[width] duration-100 ease-linear"
                style={{ width: `${recPct}%` }}
              >
                {liveRecSamples.map((v, i) => (
                  <span
                    key={`lr-${index}-${i}`}
                    className={`w-[2px] rounded-full ${clipping ? "bg-red-300 shadow-[0_0_4px_rgba(252,165,165,0.95)]" : "bg-rose-200 shadow-[0_0_3px_rgba(254,205,211,0.85)]"}`}
                    style={{ height: `${Math.max(4, v * 90)}%` }}
                  />
                ))}
              </div>
            )}
            <div
              data-testid={`wave-lane-rec-time-${index}`}
              className="pointer-events-none absolute left-2 top-1 rounded-md bg-rose-500/85 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wider text-white shadow-sm"
            >
              REC · {recordingMeter.sec.toFixed(1)}s
            </div>
            {isClipping && (
              <div
                data-testid={`wave-lane-clip-${index}`}
                className="pointer-events-none absolute left-[68px] top-1 animate-pulse rounded-md bg-rose-600 px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-[0.18em] text-white shadow-[0_0_8px_rgba(244,63,94,0.6)]"
                title="Input is clipping — lower the gain on your audio interface"
              >
                ⚠ CLIP
              </div>
            )}
            <div
              data-testid={`wave-lane-rec-peak-${index}`}
              className="pointer-events-none absolute bottom-1 right-1 flex items-center gap-1"
            >
              <span
                data-testid={`wave-lane-rec-peak-db-${index}`}
                className={`rounded bg-black/60 px-1 font-mono text-[8px] tabular-nums leading-none ring-1 ring-white/10 py-[2px] ${recPeakDbTone}`}
                title="Live peak input level. -∞ = silent · -12 = comfortable · -3 = hot · 0 = clipping"
              >
                {recPeakDb} dB
              </span>
              <div className="h-1.5 w-16 overflow-hidden rounded-full bg-black/60 ring-1 ring-white/10">
                <div
                  className="h-full bg-gradient-to-r from-emerald-400 via-amber-300 to-rose-500 transition-[width] duration-75"
                  style={{ width: `${recPeakPct}%` }}
                />
              </div>
            </div>
          </>
        )}
        <div className={`pointer-events-none absolute inset-y-0 w-[2px] bg-rose-400 ${playing ? "timeline-playhead-live" : "shadow-[0_0_10px_rgba(244,63,94,0.7)]"}`} style={playheadStyle} />
        {dragOver && (
          <div
            data-testid={`wave-lane-drop-hint-${index}`}
            className="pointer-events-none absolute inset-0 grid place-items-center bg-fuchsia-500/15 backdrop-blur-[2px]"
          >
            <span className="flex items-center gap-1.5 rounded-lg border border-fuchsia-300/40 bg-black/55 px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider text-fuchsia-100 shadow-lg">
              <UploadIcon className="h-3 w-3" /> Drop into CH {padCh(index)}{channel?.buffer ? " · will replace" : ""}
            </span>
          </div>
        )}
      </div>
      <button
        data-testid={`wave-lane-edit-${index}`}
        onClick={(e) => { e.stopPropagation(); onSelect(index); onOpenClipEditor?.(); }}
        disabled={!channel?.buffer}
        className="grid h-7 w-7 place-items-center rounded-md bg-fuchsia-500/15 text-fuchsia-200 transition hover:bg-fuchsia-500/30 disabled:opacity-30"
        title="Edit clip (trim / split)"
      >
        <Edit3 className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

/** Custom prop-equality — `channel` is the same object across renders even
 *  when its `buffer` / `name` / `waveform` mutate (engine reuses the object),
 *  so we deep-compare the inner fields that actually drive what gets drawn. */
function laneEqual(prev, next) {
  return (
    prev.index === next.index &&
    prev.selected === next.selected &&
    prev.tint === next.tint &&
    prev.playheadPct === next.playheadPct &&
    prev.recordingMeter === next.recordingMeter &&
    // Primitive snapshots — see comment in TimelinePanel.jsx.
    prev.hasBuffer === next.hasBuffer &&
    prev.channelName === next.channelName &&
    prev.waveformRev === next.waveformRev &&
    // iter-106 — invalidate when transport play state flips so the
    // playhead glow + lane sheen + empty-ghost animations start/stop.
    prev.playing === next.playing &&
    // iter-197 — invalidate at ~20 Hz when live samples are flowing so
    // the playback / recording waveforms repaint smoothly. Only ticks
    // when this lane has live audio activity, otherwise it stays stable.
    prev.liveWaveRev === next.liveWaveRev &&
    // iter-200 — flip immediately on clip-flash transition.
    prev.clipping === next.clipping &&
    // iter-201 — same for the lingering 1.5 s clip badge.
    prev.clippedRecently === next.clippedRecently
    // Callbacks are stable refs from parent.
  );
}

export default memo(WaveLane, laneEqual);
